import { useMemo } from "react"
import { useWidget } from "../../../providers/widget"
import { hostFontSizeCss } from "../../../common/host-scope"
import { BookingWidgetConfig } from "../config"

/**
 * Dark-palette overrides for the auto FIRST-PAINT path (#900). The template
 * below emits the LIGHT/resolved palette (so light output stays byte-identical);
 * this appends the DARK palette under two selector prefixes so an `auto` widget
 * on a dark OS paints dark AT PARSE — and the runtime `data-hr-theme="dark"`
 * marker keeps it after hydration:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto), plus the mirrored elevated-dialog host.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget with NO theme marker; the `:not` (kept INSIDE `:host(...)`)
 *     drops it the instant the runtime stamps the marker at hydration.
 * `.homerunner-booking-container.dark`'s `color: var(--foreground)` collapses to
 * one `:host(...)` prefix (the host marker covers the whole shadow tree, and
 * --foreground already flips under these same selectors in styles/common.css).
 * The dark accent is read from options.darkModeColors directly — NOT the
 * resolvedTheme-gated `accentColor` — so it ships on every render.
 *
 * Selector symmetry matters: the prefixes are `:where(...)`-wrapped
 * (zero specificity) so the cascade matches what a dark render always
 * emitted, which means each dark rule must use the SAME descendant selector
 * as its light counterpart — `.homerunner-booking-container .checkout-button`,
 * not a bare `.checkout-button` — to tie it and win by source order.
 *
 * The no-dark-accent branch is not empty (review finding): the light/resolved
 * emission above may have painted the LIGHT accent (SSR of an auto widget
 * resolves light), and a hydrated dark render with an empty dark accent shows
 * booking.css's base values — so dark first paint must override back to those
 * base values (#f59e0c button, inherited rent-amount color) or the button
 * flashes light-accent → stock at hydration.
 */
function bookingDarkOverrides(prefix: string, darkAccent: string | undefined): string {
  return `
    ${prefix} .homerunner-booking-container {
      color: var(--foreground) !important;
    }
    ${darkAccent ? `
    ${prefix} .homerunner-booking-container .checkout-button {
      background-color: ${darkAccent};
    }
    ${prefix} .homerunner-booking-container .rent-amount {
      color: ${darkAccent};
    }
    ` : `
    ${prefix} .homerunner-booking-container .checkout-button {
      background-color: #f59e0c; /* booking.css base — what hydrated dark shows */
    }
    ${prefix} .homerunner-booking-container .rent-amount {
      color: inherit; /* booking.css declares no color — matches hydrated dark */
    }
    `}
  `
}

export function useBookingCss(): string {
  const { options, resolvedTheme, widgetId } = useWidget<BookingWidgetConfig>()

  const accentColor = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors.accent : options.lightModeColors.accent
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  // Dark accent for the appended overrides, read from the DARK color set
  // directly so it is present regardless of the resolved theme (auto
  // first-paint can't wait for resolvedTheme to flip).
  const darkAccentColor = useMemo(
    () => options.darkModeColors.accent,
    [options.darkModeColors.accent],
  )

  return useMemo(() => `
    .homerunner-booking-container *:not([class^=icon-]) {
      font-family: '${options.font.family}' !important;
    }
    .homerunner-booking-container.dark {
      color: var(--foreground) !important;
    }
    ${hostFontSizeCss(widgetId, options.font.size)}
    ${accentColor ? `
    .homerunner-booking-container .checkout-button {
      background-color: ${accentColor};
    }
    .homerunner-booking-container .rent-amount {
      color: ${accentColor};
    }
    ` : ''}
    ${bookingDarkOverrides(':where(:host([data-hr-theme="dark"]))', darkAccentColor)}
    @media (prefers-color-scheme: dark) {
      ${bookingDarkOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', darkAccentColor)}
    }
    ${options.customCss ?? ''}
  `, [widgetId, options.font.family, options.font.size, accentColor, darkAccentColor, options.customCss])
}
